import HTMLParser from "html-react-parser";
import * as React from "react";
import styled, { css } from "styled-components";
import * as moment from "moment-timezone";
import DOMPurify from "dompurify";
import { decode } from "html-entities";

import Grid from "src/library/components/Hedron";
import { Datetime } from "src/library/components/Datetime";
import { Icon } from "src/library/components/Icon";
import { ButtonLink } from "src/library/components/Link";
import HtmlBody from "src/library/components/Notes/HtmlBody";
import {
  Text,
  TextTruncate
} from "src/library/components/Text";
import { Icons } from "src/library/components/Icon/types";
import Button from "src/library/components/Button/Button";
import useOrder from "src/core/orders/hooks/useOrder";
import { OrderOrigin } from "src/core/orders/api/orders.api";

import { INotification } from "../api/notifications.api";

interface IProps {
  notification: INotification;
  markAsRead: (notificationId: number) => void;

}

const NotificationReveal: React.ReactFCWithChildren<IProps> = ({ notification, markAsRead }) => {
  const [ showDetails, setShowDetails ] = React.useState(false);
  const readAt = notification.notificationUsers && notification.notificationUsers[ 0 ] ? notification.notificationUsers[ 0 ].readAt : null;
  const [ , { createOrder } ] = useOrder();

  const decodedBody = React.useMemo(() => {
    const decodedHTML = decode(notification.body);

    return DOMPurify.sanitize(decodedHTML);
  }, [ notification.body ]);

  const dataButton = React.useCallback(() => {
    if (notification.data) {
      if (notification.data.supportTicketId) {
        <ButtonLink
          className="notification-button"
          to={`/support/tickets/${notification.data.supportTicketId}`}
        >
          View Ticket
        </ButtonLink>;
      } else if (notification.data.orderId) {
        return (
          <ButtonLink
            className="notification-button"
            to={`/orders/${notification.data.orderId}`}
          >
            View Order
          </ButtonLink>
        );
      } else if (notification.data.listId) {
        return (
          <ButtonLink
            className="notification-button"
            to={`/lists/${notification.data.listId}`}
          >
            View List
          </ButtonLink>
        );
      } else if (notification.data.orderDeliveryDate) {
        return (
          <Button
            className="notification-button"
            onClick={() => {
              createOrder(moment.utc(notification.data.orderDeliveryDate, "YYYY-MM-DD").toDate(), OrderOrigin.Reminder);
            }}
          >
            Create order
          </Button>
        );
      }
    }

    return null;
  }, [ createOrder, notification.data ]);

  return (
    <NotificationRevealWrapper direction="vertical">
      <Grid.Box
        className="title-wrapper"
        onClick={() => {
          if (!readAt && showDetails === false) {
            markAsRead(notification.id);
          }

          setShowDetails(!showDetails);
        }}
      >
        {!readAt && (
          <Icon
            className="unread-icon"
            size={10}
            icon={Icons.CIRCLE}
            $fillColor="warn"
          />
        )}

        <Text
          as="h3"
          weight="bold"
        >
          {notification.title}
        </Text>

        <Icon
          $fillColor="primaryLight"
          className="arrow-icon"
          icon={showDetails ? Icons.CHEVRONUP : Icons.CHEVRONDOWN}
        />
      </Grid.Box>

      <Grid.Box className="details-wrapper">
        {!showDetails ? (
          <TextTruncate truncateProps={{ lines: 2 }}>
            <Text>
              {HTMLParser(decodedBody)}
            </Text>
          </TextTruncate>
        ) : (
          <>
            {" "}

            <HtmlBody body={notification.body} />

            {dataButton()}
          </>
        )}
      </Grid.Box>

      <Grid.Box className="details-wrapper">
        <Text
          color="secondary"
          fontSize="sub"
        >
          Sent at:
          {" "}

          <Datetime
            date={notification.createdAt}
            template="time"
          />

          {" "}
          on

          {" "}

          <Datetime date={notification.createdAt} />
        </Text>

        {readAt && (
          <Text
            color="secondary"
            fontSize="sub"
          >
            {" | Read at:"}

            {" "}

            <Datetime
              date={readAt}
              template="time"
            />

            {" "}
            on

            {" "}

            <Datetime date={readAt} />
          </Text>
        )}
      </Grid.Box>
    </NotificationRevealWrapper>
  );
};

export default NotificationReveal;

const NotificationRevealWrapper = styled(Grid.Box)`
  ${({ theme }) => css`
    padding-bottom: ${theme.spacing.padding.md};
    padding: ${theme.spacing.padding.md} calc(${theme.spacing.padding.sm} + 25px);
    border-top: 1px solid ${theme.colors.primaryLight};

    .title-wrapper {
      position: relative;
      cursor: pointer;
    }

    .unread-icon {
      position: absolute;
      right: calc(${theme.spacing.padding.sm} + 100%);
      top: 50%;
      transform: translateY(-50%);
    }

    .arrow-icon {
      position: absolute;
      left: calc(100%);
      top: 50%;
      transform: translateY(-50%);
    }

    .details-wrapper {
      padding-bottom: 0;
    }

    .notification-button {
      margin: ${theme.spacing.padding.md} 0;
      width: 150px;
    }
  `}
`;
